/**
 * Inbox — per `rcp-inbox.html`.
 *
 * NotificationLog filtered to the current CIF (server-scoped).
 *
 * A11y fixes (mockup #10):
 *   - Rows are `<button>` elements (keyboard-focusable).
 *   - Unread indicator: NOT color-only — visually-hidden "unread" text
 *     accompanies the red dot; `aria-label` on each row combines subject +
 *     state + date.
 */

import { useEffect, useState } from 'react';
import type { NotificationLogDto, Iso8601 } from '@retail-ops/shared-types';
import { getApiClient } from '../../services/apiClient';
import { useToast } from '../../components/a11y/AccessibleToast';
import { Mail, MailOpen } from '@retail-ops/panel-shell/fi';

export function Inbox(): JSX.Element {
  const { pushToast } = useToast();
  const [items, setItems] = useState<ReadonlyArray<NotificationLogDto>>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        // TODO(PLANNED /portal/inbox): route lists messages for this CIF.
        const res = await getApiClient().portal.inbox.list({ top: 50, signal: controller.signal });
        if (res.success && res.data) setItems(res.data);
        else pushToast('error', 'Could not load your inbox', res.error ?? 'Please refresh.');
      } catch (e) {
        if ((e as Error)?.name !== 'AbortError') {
          pushToast('error', 'Could not load your inbox', 'Please check your connection.');
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [pushToast]);

  async function onOpen(n: NotificationLogDto): Promise<void> {
    setActiveId(n.notificationId);
    if (!n.readAt) {
      try {
        await getApiClient().portal.inbox.markRead(n.notificationId);
        setItems((cur) =>
          cur.map((x) =>
            x.notificationId === n.notificationId
              ? { ...x, readAt: new Date().toISOString() as Iso8601 }
              : x,
          ),
        );
      } catch {
        /* non-fatal */
      }
    }
  }

  const active = items.find((x) => x.notificationId === activeId) ?? null;

  return (
    <section aria-labelledby="inbox-heading">
      <h1 id="inbox-heading" tabIndex={-1}>Inbox</h1>
      <p className="lead">Everything we&apos;ve sent you recently.</p>

      <div className="section-grid">
        <div className="card" aria-label="Messages">
          {loading ? (
            <p aria-live="polite">Loading your messages...</p>
          ) : items.length === 0 ? (
            <p style={{ color: 'var(--ink-500)' }}>No messages yet.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-label="Inbox messages">
              {items.map((n) => {
                const unread = !n.readAt;
                const when = n.sentAt ?? n.queuedAt;
                const whenLabel = when ? new Date(when).toLocaleString() : '';
                const label = `${unread ? 'Unread: ' : ''}${n.subject ?? n.category}, ${whenLabel}`;
                return (
                  <li key={n.notificationId} style={{ borderBottom: '1px solid var(--surface-border)' }}>
                    <button
                      type="button"
                      onClick={() => void onOpen(n)}
                      aria-label={label}
                      aria-current={activeId === n.notificationId ? 'true' : undefined}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '28px 1fr auto',
                        gap: 12,
                        padding: '12px 4px',
                        width: '100%',
                        textAlign: 'left',
                        background: activeId === n.notificationId ? 'var(--surface-info-bg)' : 'transparent',
                      }}
                    >
                      {unread ? (
                        <Mail aria-hidden="true" size={18} color="var(--pcb-blue)" />
                      ) : (
                        <MailOpen aria-hidden="true" size={18} color="var(--ink-500)" />
                      )}
                      <span>
                        <span style={{ fontWeight: unread ? 500 : 400, color: 'var(--pcb-blue-dark)' }}>
                          {n.subject ?? n.category}
                        </span>
                        {unread ? <span className="visually-hidden"> unread</span> : null}
                        <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>{n.channel}</div>
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>{whenLabel}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <aside className="card" aria-labelledby="inbox-detail-heading" aria-live="polite">
          <h2 id="inbox-detail-heading">Message</h2>
          {active ? (
            <>
              <h3 style={{ marginTop: 0 }}>{active.subject ?? active.category}</h3>
              <p style={{ fontSize: 13, color: 'var(--ink-500)' }}>
                {active.channel} &middot; {active.sentAt ? new Date(active.sentAt).toLocaleString() : ''}
              </p>
              <p>Message body would render here (from template + variables).</p>
            </>
          ) : (
            <p style={{ color: 'var(--ink-500)' }}>Select a message to read.</p>
          )}
        </aside>
      </div>
    </section>
  );
}
